import { fmtDate, fmtTime, numWords, COMPANY } from './format';
import { LOGO_DATA_URL } from '../assets/logo';

/**
 * Opens a new browser window with a print-ready version of the invoice
 * and triggers the browser print dialog, matching the original prototype's
 * printInvoice() behavior.
 */
export function printInvoice(invoice) {
  const subtotal = invoice.subtotal ?? (invoice.items || []).reduce((s, i) => s + parseFloat(i.line_total ?? i.total ?? 0), 0);
  const tax = invoice.tax || 0;
  const discount = invoice.discount || 0;
  const total = invoice.total;

  const previousDue = parseFloat(invoice.previous_due || 0);
  const amountPaid = invoice.amount_paid != null ? parseFloat(invoice.amount_paid) : null;
  const balanceDue = parseFloat(invoice.balance_due || 0);
  const showPayment = amountPaid != null && (previousDue > 0 || balanceDue > 0 || amountPaid < parseFloat(total));
  const paymentRows = showPayment
    ? `${previousDue > 0
        ? `<tr><td>Previous Dues</td><td>Rs. ${previousDue.toFixed(2)}</td></tr>` +
          `<tr><td>Total Payable</td><td>Rs. ${(previousDue + parseFloat(total)).toFixed(2)}</td></tr>`
        : ''}` +
      `<tr><td>Amount Paid</td><td>Rs. ${amountPaid.toFixed(2)}</td></tr>` +
      `<tr style="font-weight:700;color:${balanceDue > 0 ? '#e53935' : '#2E7D32'}"><td>Balance Due</td><td>Rs. ${balanceDue.toFixed(2)}</td></tr>`
    : '';

  const w = window.open('', '_blank', 'width=800,height=1000');
  if (!w) {
    alert('Please allow popups for this site to print invoices.');
    return;
  }

  const itemsRows = (invoice.items || [])
    .map(
      (it, i) => `<tr><td>${i + 1}</td><td style="font-weight:600">${it.product_name || it.name}</td><td>Rs. ${parseFloat(it.mrp).toFixed(2)}</td><td>${it.base_pack ?? it.basePack}</td><td>${it.qty}</td><td>Rs. ${parseFloat(it.price).toFixed(2)}</td><td>Rs. ${parseFloat(it.line_total ?? it.total).toFixed(2)}</td></tr>`
    )
    .join('');

  w.document.write(`<!DOCTYPE html><html><head><title>${invoice.invoice_number}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap');
    body{font-family:Arial,sans-serif;font-size:12px;color:#212121;padding:24px 24px 72px;max-width:750px;margin:0 auto}
    .header{position:relative;display:flex;flex-direction:column;align-items:center;margin-bottom:16px}
    .brand{display:flex;flex-direction:column;align-items:center;text-align:center}
    .brand-email{font-size:11px;color:#616161;margin-top:6px}
    .inv-title-block{position:absolute;top:0;right:0;text-align:right}
    .inv-title{font-family:'Poppins',Arial,sans-serif;font-size:52px;font-weight:400;color:#2E7D32;text-align:right;letter-spacing:1px;text-transform:uppercase;line-height:1}
    .inv-num{font-size:20px;font-weight:600;color:#1A237E;text-align:right}
    .addr-box{display:grid;grid-template-columns:1fr 1fr;gap:12px;background:#E8F5E9;padding:10px 14px;border-radius:6px;margin-bottom:12px}
    .addr-label{font-size:9px;font-weight:700;color:#2E7D32;text-transform:uppercase;letter-spacing:.05em;margin-bottom:4px}
    .addr-name{font-size:13px;font-weight:700;color:#1A237E}
    .addr-detail{font-size:10px;color:#616161;line-height:1.6}
    .meta{display:grid;grid-template-columns:repeat(3,1fr);border:1px solid #2E7D32;border-radius:6px;overflow:hidden;margin-bottom:12px}
    .meta-cell{padding:6px 10px;border-right:1px solid #2E7D32}
    .meta-cell:last-child{border-right:none}
    .meta-label{font-size:9px;font-weight:700;color:#2E7D32;text-transform:uppercase;letter-spacing:.04em}
    .meta-val{font-size:11px;font-weight:600;color:#1A237E;margin-top:2px}
    .items-table{width:100%;border-collapse:collapse;font-size:11px;margin-bottom:12px}
    .items-table th{background:#2E7D32;color:#fff;padding:6px 8px;text-align:left;font-size:10px;text-transform:uppercase}
    .items-table td{padding:5px 8px;border-bottom:1px solid #eee}
    .items-table tr:nth-child(even) td{background:#fafafa}
    .totals-box{width:auto;min-width:260px;border-collapse:collapse}
    .totals-box td{padding:5px 8px;font-size:11px;border-bottom:1px solid #eee;background:none}
    .totals-box td:last-child{text-align:right}
    .totals-box tr:last-child td{border-bottom:none}
    .grand td{background:#2E7D32 !important;color:#fff !important;font-size:14px;font-weight:700;padding:9px 10px}
    .words{font-size:10px;color:#616161;font-style:italic;margin-bottom:8px}
    .thank{color:#2E7D32;font-weight:700;font-size:11px}
    .footer-icons{display:flex;gap:24px;justify-content:center;padding:10px 0;border-top:1px dashed #e0e0e0;border-bottom:1px dashed #e0e0e0;margin:10px 0}
    .f-icon{text-align:center;font-size:10px;color:#616161}
    .footer-addr{position:fixed;left:24px;right:24px;bottom:16px;text-align:center;font-size:10px;color:#9e9e9e;padding-top:8px;border-top:1px dashed #e0e0e0}
    .divider{border:none;border-top:1px solid #e0e0e0;margin:10px 0}
    @media print{body{padding:12px 12px 72px}.footer-addr{left:12px;right:12px;bottom:12px}}
  </style></head><body>
  <div class="header">
    <div class="inv-title-block"><div class="inv-title">INVOICE</div><div class="inv-num"># ${invoice.invoice_number}</div><div style="text-align:right;font-size:15px;color:#616161">Date: ${fmtDate(invoice.invoice_date)}<br>Time: ${fmtTime(invoice.invoice_date)}</div></div>
    <div class="brand">
      <img src="${LOGO_DATA_URL}" alt="Easy1Basket" style="height:135px;width:auto;display:block" />
      <div class="brand-email">${COMPANY.email}</div>
    </div>
  </div>
  <div class="addr-box">
    <div><div class="addr-label">Bill To</div><div class="addr-name">${invoice.customer_name || 'Walk-in Customer'}</div><div class="addr-detail">${invoice.customer_address || ''}<br>${invoice.customer_phone ? 'Phone: ' + invoice.customer_phone : ''}</div></div>
    <div><div class="addr-label">Bill From</div><div class="addr-name">${COMPANY.name}</div><div class="addr-detail">${COMPANY.address}<br>${COMPANY.city}<br>Phone: ${COMPANY.phone}<br>GST: ${invoice.gst_number || '—'}</div></div>
  </div>
  <div class="meta">
    <div class="meta-cell"><div class="meta-label">Order Date</div><div class="meta-val">${fmtDate(invoice.invoice_date)}</div></div>
    <div class="meta-cell"><div class="meta-label">Order Time</div><div class="meta-val">${fmtTime(invoice.invoice_date)}</div></div>
    <div class="meta-cell"><div class="meta-label">Order By</div><div class="meta-val" style="font-size:9px">${invoice.customer_name || 'Walk-in'}${invoice.customer_phone ? ' (' + invoice.customer_phone + ')' : ''}</div></div>
  </div>
  <table class="items-table"><thead><tr><th>#</th><th>Item</th><th>MRP</th><th>Base Pack</th><th>Qty</th><th>Price</th><th>Total</th></tr></thead><tbody>
  ${itemsRows}
  </tbody></table>
  <div style="display:flex;justify-content:space-between;align-items:flex-end;flex-wrap:wrap;gap:12px">
    <div>
      <div style="font-size:9px;font-weight:700;color:#2E7D32;text-transform:uppercase;margin-bottom:4px">Total Amount in Words</div>
      <div class="words">${numWords(total)}</div>
      <div class="thank">THANK YOU FOR SHOPPING WITH EASY1BASKET!</div>
    </div>
    <table class="totals-box">
      <tr><td>Sub Total</td><td>Rs. ${parseFloat(subtotal).toFixed(2)}</td></tr>
      <tr><td>Discount</td><td>Rs. ${parseFloat(discount).toFixed(2)}</td></tr>
      <tr><td>Tax (GST${invoice.tax_rate ? ` ${parseFloat(invoice.tax_rate).toFixed(2)}%` : ''})</td><td>Rs. ${parseFloat(tax).toFixed(2)}</td></tr>
      <tr class="grand"><td>Grand Total</td><td>Rs. ${parseFloat(total).toFixed(2)}</td></tr>
      ${paymentRows}
    </table>
  </div>
  <div class="footer-addr"><strong>${COMPANY.name}</strong> | ${COMPANY.address}, ${COMPANY.city} | Phone: ${COMPANY.phone} | GST: ${invoice.gst_number || '—'}</div>
  <script>window.onload=function(){window.print()}<\/script>
  </body></html>`);
  w.document.close();
}
